// frontend/app/components/lessonPlans/present/usePresenterWindow.js
//
// Owns the second screen: the popup, the channel, and every way both of them
// can end.
//
// THE POPUP IS THE PRESENTER VIEW, NOT THE LESSON. The main window keeps the
// lesson — full screen, chrome-free, already working, with the 3D player, the
// video and the Saras rail inside it. Only the notes move. Inverting that
// (lesson in the popup) would push every player and fullscreen through a second
// window for nothing, and would break the one thing present mode already does
// well.
//
// THE LIFECYCLE IS THE FEATURE. A presenter window that is merely opened is
// easy; what makes this safe to hand a teacher is what happens at the edges:
//
//   blocked        window.open returns null with no exception. The caller is
//                  told, in words a teacher can act on. Never silent.
//   popup closed   the lesson carries on and the button offers to reopen.
//                  Detected by polling `closed`, because a window closed from
//                  its own title bar tells the opener nothing.
//   lesson ends    unmount closes the popup. An orphaned window still showing
//                  notes for a finished lesson is worse than no window at all,
//                  because the next lesson's teacher will believe it.
//   opener dies    handled on the popup's side (see presenterWindowDoc.js) —
//                  the only case this window is not alive to clean up after.
//
// Nothing here writes a lesson response, an evaluation or mastery, and it has
// no way to: the intents it accepts are exactly previous / next / reveal /
// advance, and each one is dispatched to a callback the caller already uses for
// its own on-screen controls. Present mode's rule is unchanged — there are no
// devices in the room, so there is no answer to record.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Platform } from "react-native";

import {
  PRESENTER_MESSAGE,
  buildPresenterLabels,
  buildPresenterState,
  closePresenterChannel,
  isPresenterMessageFor,
  makePresenterSessionId,
  openPresenterChannel,
  postPresenterMessage,
  supportsPresenterChannel,
} from "./presenterChannel";
import { buildPresenterDoc } from "./presenterWindowDoc";

/**
 * A named window, so a second press reuses the one that already exists rather
 * than scattering presenter windows across the desktop — including in the case
 * where our own handle was lost to a hot reload.
 */
const PRESENTER_WINDOW_NAME = "sarasLessonPresenter";

/**
 * Portrait-ish and modest. It opens on the laptop beside the projector output,
 * and a teacher drags it where they want it; opening it maximised would cover
 * whatever they were doing and start the feature with a fight.
 */
const PRESENTER_WINDOW_FEATURES =
  "width=560,height=780,menubar=no,toolbar=no,location=no,status=no,scrollbars=yes,resizable=yes";

/**
 * How often we ask whether the popup is still there.
 *
 * There is no event for "the user closed my popup" — `beforeunload` inside the
 * popup covers the normal path and this covers the rest (a crash, a force
 * quit, a browser that skips unload handlers on window close). Under a second
 * so the button never sits there claiming a window that is gone.
 */
const CLOSE_POLL_MS = 700;

/** closed → the teacher has no presenter window; blocked → the browser refused. */
export const PRESENTER_STATUS = {
  CLOSED: "closed",
  OPEN: "open",
  BLOCKED: "blocked",
};

const usePresenterWindow = ({
  lesson,
  steps,
  index = 0,
  revealCount = 0,
  isDarkMode = false,
  // The same three moves the on-screen controls make. Passing the caller's own
  // handlers rather than raw setters is what keeps the two windows honest: the
  // popup cannot reach anything the teacher could not already press.
  onPrevious,
  onNext,
  onReveal,
  onAdvance,
  /**
   * Fired when a presenter window opens or closes.
   *
   * Optional, and only a caller that has to know needs it — opening a popup can
   * drop the main window out of browser fullscreen, and whoever treats leaving
   * fullscreen as "the teacher is done" has to not act on that one.
   */
  onOpenChange,
  enabled = true,
  // The popup is plain HTML and cannot reach the app's i18n, so its own copy
  // has to be translated here and travel with the state.
  t,
}) => {
  // Feature-detected rather than trusted from Platform alone: react-native-web
  // is the only place this runs, but a jsdom test and a server render both have
  // a `window` that cannot open one.
  const isWeb =
    Platform.OS === "web" &&
    typeof window !== "undefined" &&
    typeof window.open === "function";

  const [status, setStatus] = useState(PRESENTER_STATUS.CLOSED);

  const windowRef = useRef(null);
  const channelRef = useRef(null);
  const pollRef = useRef(null);
  const sessionIdRef = useRef(null);
  if (sessionIdRef.current === null) sessionIdRef.current = makePresenterSessionId();

  // The lesson clock starts when present mode mounts, not when the presenter
  // window opens. A teacher who opens their notes ten minutes in wants to know
  // they are ten minutes in.
  const startedAtRef = useRef(Date.now());

  const handlersRef = useRef({});
  handlersRef.current = { onPrevious, onNext, onReveal, onAdvance };

  const onOpenChangeRef = useRef(onOpenChange);
  onOpenChangeRef.current = onOpenChange;

  const live = isWeb && supportsPresenterChannel();

  const labels = useMemo(() => buildPresenterLabels(t), [t]);

  const state = useMemo(
    () =>
      buildPresenterState({
        lesson,
        steps,
        index,
        revealCount,
        isDarkMode,
        live,
        labels,
        sessionId: sessionIdRef.current,
        startedAt: startedAtRef.current,
      }),
    [lesson, steps, index, revealCount, isDarkMode, live, labels]
  );

  // Read by the HELLO reply, which arrives from another window and therefore
  // outside any render this closure was created in.
  const stateRef = useRef(state);
  stateRef.current = state;

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  /**
   * Let go of the popup and the channel.
   *
   * `closeWindow` is false for "it closed itself" and true for "we are done
   * with it". Idempotent, because every path out of present mode lands here and
   * several of them land here twice.
   */
  const teardown = useCallback(
    (closeWindow) => {
      stopPolling();

      const channel = channelRef.current;
      const popup = windowRef.current;
      channelRef.current = null;
      windowRef.current = null;

      if (channel) {
        if (closeWindow) {
          postPresenterMessage(channel, {
            type: PRESENTER_MESSAGE.ENDED,
            sessionId: sessionIdRef.current,
          });
        }
        closePresenterChannel(channel);
      }

      // The direct close is the one that actually matters: it works with no
      // channel at all, which is what makes "the popup dies with the lesson"
      // true even in the degraded case. The ENDED message above is only a
      // second route for a handle that has gone stale.
      if (closeWindow && popup && !popup.closed) {
        try {
          popup.close();
        } catch (err) {
          /* already gone */
        }
      }
    },
    [stopPolling]
  );

  const markClosed = useCallback(() => {
    setStatus(PRESENTER_STATUS.CLOSED);
    if (onOpenChangeRef.current) onOpenChangeRef.current(false);
  }, []);

  const handleMessage = useCallback(
    (event) => {
      const data = event && event.data;
      if (!isPresenterMessageFor(sessionIdRef.current, data)) return;

      if (data.type === PRESENTER_MESSAGE.HELLO) {
        // A window that has just opened knows only what was baked into its
        // document. Answering immediately is what makes "opened on step 9 shows
        // step 9" true even when the seed is a frame behind.
        postPresenterMessage(channelRef.current, {
          type: PRESENTER_MESSAGE.STATE,
          sessionId: sessionIdRef.current,
          state: stateRef.current,
        });
        return;
      }

      if (data.type === PRESENTER_MESSAGE.BYE) {
        teardown(false);
        markClosed();
        return;
      }

      if (data.type !== PRESENTER_MESSAGE.INTENT) return;

      const handlers = handlersRef.current;
      switch (data.action) {
        case "previous":
          if (handlers.onPrevious) handlers.onPrevious();
          break;
        case "next":
          if (handlers.onNext) handlers.onNext();
          break;
        case "reveal":
          if (handlers.onReveal) handlers.onReveal();
          break;
        case "advance":
          if (handlers.onAdvance) handlers.onAdvance();
          break;
        default:
          // An action from a newer build of the popup. Ignored rather than
          // guessed at — guessing moves a slide in front of a class.
          break;
      }
    },
    [markClosed, teardown]
  );

  /**
   * Opens the presenter window.
   *
   * MUST be called straight from the press or keypress that asked for it: a
   * pop-up blocker allows `window.open` only while the user gesture is still on
   * the stack, exactly like the Fullscreen API. Never defer this into an effect.
   *
   * @returns {boolean} whether a window is now open.
   */
  const open = useCallback(() => {
    if (!isWeb || !enabled) return false;

    const existing = windowRef.current;
    if (existing && !existing.closed) {
      // Already open — the teacher has probably lost it behind the lesson.
      try {
        existing.focus();
      } catch (err) {
        /* focus is a courtesy, not a requirement */
      }
      return true;
    }

    // The channel comes first so the popup's HELLO — which can arrive before
    // window.open has even returned on a fast machine — has somewhere to land.
    const channel = openPresenterChannel();
    if (channel) channel.onmessage = handleMessage;
    channelRef.current = channel;

    let popup = null;
    try {
      popup = window.open("", PRESENTER_WINDOW_NAME, PRESENTER_WINDOW_FEATURES);
    } catch (err) {
      popup = null;
    }

    if (!popup) {
      // Blocked, or opened with no gesture behind it. Say so — a control that
      // appears to do nothing is the thing a teacher gives up on.
      closePresenterChannel(channel);
      channelRef.current = null;
      setStatus(PRESENTER_STATUS.BLOCKED);
      return false;
    }

    windowRef.current = popup;

    try {
      const doc = buildPresenterDoc({ ...stateRef.current, live: !!channel });
      popup.document.open();
      popup.document.write(doc);
      popup.document.close();
    } catch (err) {
      // Writing into our own same-origin popup should not fail; if it somehow
      // does, an empty window is worse than none.
      teardown(true);
      setStatus(PRESENTER_STATUS.BLOCKED);
      return false;
    }

    stopPolling();
    pollRef.current = setInterval(() => {
      const current = windowRef.current;
      if (!current || current.closed) {
        teardown(false);
        markClosed();
      }
    }, CLOSE_POLL_MS);

    setStatus(PRESENTER_STATUS.OPEN);
    // Reported synchronously rather than from an effect, because the caller may
    // need it before the browser's own fullscreenchange lands.
    if (onOpenChangeRef.current) onOpenChangeRef.current(true);
    return true;
  }, [enabled, handleMessage, isWeb, markClosed, stopPolling, teardown]);

  const close = useCallback(() => {
    teardown(true);
    markClosed();
  }, [markClosed, teardown]);

  const toggle = useCallback(() => {
    const popup = windowRef.current;
    if (popup && !popup.closed) {
      close();
      return false;
    }
    return open();
  }, [close, open]);

  /** Puts the blocked notice away without trying again. */
  const dismissBlocked = useCallback(() => {
    setStatus((current) =>
      current === PRESENTER_STATUS.BLOCKED ? PRESENTER_STATUS.CLOSED : current
    );
  }, []);

  // Every state change goes out as a whole snapshot. Cheap — one small object
  // per step or reveal, which is a handful of messages per lesson.
  useEffect(() => {
    if (status !== PRESENTER_STATUS.OPEN) return;
    postPresenterMessage(channelRef.current, {
      type: PRESENTER_MESSAGE.STATE,
      sessionId: sessionIdRef.current,
      state,
    });
  }, [state, status]);

  // The lesson is over: the teacher pressed Exit, the lesson closed under them,
  // or the module switched. All of those unmount this, and none of them may
  // leave a window on the laptop showing a lesson that has ended.
  useEffect(
    () => () => {
      teardown(true);
      if (onOpenChangeRef.current) onOpenChangeRef.current(false);
    },
    [teardown]
  );

  return {
    /** Whether a presenter window is something this platform can offer at all. */
    supported: isWeb,
    isOpen: status === PRESENTER_STATUS.OPEN,
    isBlocked: status === PRESENTER_STATUS.BLOCKED,
    /** False where the two windows cannot be linked; the popup says so itself. */
    live,
    open,
    close,
    toggle,
    dismissBlocked,
  };
};

export default usePresenterWindow;
